import React, { useEffect, useMemo, useState } from "react";
import {
    Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
    DialogContent, DialogTitle, Divider, FormControl, IconButton,
    InputLabel, ListSubheader, MenuItem, Select, TextField, Typography
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditAttributesIcon from '@mui/icons-material/EditAttributes';
import { useDispatch, useSelector } from "react-redux";
import {
    fluxoObservado,
    velocidade,
    caracteristicasDaVia,
    caracteristicasDaVia2,
    margemDaVia,
    intersecoes,
    instalacoesUVV,
    instalacoesUVV2,
} from "../../environments/labels";
import { applyBatchEdit } from "../../store/reducers/dadosSegmentos";

const AUTO_FIELDS = [
    "Curvatura",
    "Inclinação",
    "Data de coleta de imagens em campo",
];

const ATTRIBUTE_GROUPS = [
    { label: "Fluxo observado", fields: fluxoObservado },
    { label: "Velocidade", fields: velocidade },
    { label: "Características da via", fields: caracteristicasDaVia },
    { label: "Características da via 2", fields: caracteristicasDaVia2 },
    { label: "Atributos da margem da via", fields: margemDaVia },
    { label: "Interseções", fields: intersecoes },
    { label: "Instalações UVV", fields: instalacoesUVV },
    { label: "Instalações UVV 2", fields: instalacoesUVV2 },
];

function parsePages(input, maxPage) {
    const tokens = String(input || "").split(",").map((t) => t.trim()).filter(Boolean);
    const pages = new Set();
    const invalids = [];

    for (const tok of tokens) {
        if (tok.includes("-")) {
            const parts = tok.split("-").map((s) => s.trim());
            if (parts.length !== 2) { invalids.push(tok); continue; }
            const a = parseInt(parts[0], 10);
            const b = parseInt(parts[1], 10);
            if (isNaN(a) || isNaN(b) || a < 1 || b > maxPage || a > b) {
                invalids.push(tok);
                continue;
            }
            for (let i = a; i <= b; i++) pages.add(i);
        } else {
            const n = parseInt(tok, 10);
            if (isNaN(n) || n < 1 || n > maxPage) { invalids.push(tok); continue; }
            pages.add(n);
        }
    }

    return { pages: Array.from(pages).sort((a, b) => a - b), invalids };
}

const BatchEditModal = ({ open, onClose }) => {
    const dispatch = useDispatch();
    const totalPages = useSelector((s) => s.dadosSegmentos.length);
    const currentPage = useSelector((s) => s.dadosSegmentos.paginaAtual);

    const [attribute, setAttribute] = useState("");
    const [newValue, setNewValue] = useState("");
    const [pageMode, setPageMode] = useState("all");
    const [pagesInput, setPagesInput] = useState("");
    const [applying, setApplying] = useState(false);

    // Reset ao abrir
    useEffect(() => {
        if (!open) return;
        setAttribute("");
        setNewValue("");
        setPageMode("all");
        setPagesInput("");
        setApplying(false);
    }, [open]);

    const editableGroups = useMemo(() => {
        return ATTRIBUTE_GROUPS.map((g) => ({
            label: g.label,
            fields: g.fields
                .filter((f) => Array.isArray(f.label) && f.label.length > 0)
                .filter((f) => !AUTO_FIELDS.includes(f.title)),
        })).filter((g) => g.fields.length > 0);
    }, []);

    const selectedField = useMemo(() => {
        for (const g of editableGroups) {
            const found = g.fields.find((f) => f.varName === attribute);
            if (found) return found;
        }
        return null;
    }, [attribute, editableGroups]);

    const { pages, invalids } = useMemo(() => {
        if (pageMode === "all") {
            return { pages: Array.from({ length: totalPages }, (_, i) => i + 1), invalids: [] };
        }
        if (pageMode === "current") {
            return { pages: [currentPage], invalids: [] };
        }
        return parsePages(pagesInput, totalPages);
    }, [pageMode, pagesInput, totalPages, currentPage]);

    const canApply =
        !!attribute && newValue !== "" && pages.length > 0 && invalids.length === 0;

    const handleApply = () => {
        if (!canApply || !selectedField) return;
        setApplying(true);

        const chosen = selectedField.label.find(
            (o) => String(o.code) === String(newValue)
        );

        dispatch(
            applyBatchEdit({
                title: selectedField.title,
                newValue: {
                    title: chosen?.title ?? "",
                    code: chosen?.code ?? newValue,
                },
                pages,
            })
        );

        setTimeout(() => {
            setApplying(false);
            onClose();
        }, 150);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    pb: 1.5,
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <EditAttributesIcon sx={{ color: "var(--goia-navy)" }} />
                    <Typography variant="h6" fontWeight={700}>
                        Edição em Lote
                    </Typography>
                </Box>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ pt: 3 }}>
                {/* 1. Atributo */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                        Qual atributo deseja modificar?
                    </Typography>
                    <FormControl fullWidth size="small">
                        <InputLabel>Selecione um atributo</InputLabel>
                        <Select
                            value={attribute}
                            label="Selecione um atributo"
                            onChange={(e) => {
                                setAttribute(e.target.value);
                                setNewValue("");
                            }}
                            MenuProps={{ PaperProps: { sx: { maxHeight: 400 } } }}
                        >
                            {editableGroups.flatMap((g) => [
                                <ListSubheader
                                    key={`grp-${g.label}`}
                                    sx={{ fontWeight: 700, color: "var(--goia-navy)" }}
                                >
                                    {g.label}
                                </ListSubheader>,
                                ...g.fields.map((f) => (
                                    <MenuItem key={f.varName} value={f.varName} sx={{ pl: 3 }}>
                                        {f.title}
                                    </MenuItem>
                                )),
                            ])}
                        </Select>
                    </FormControl>
                </Box>

                {/* 2. Nova resposta */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                        Qual será a nova resposta?
                    </Typography>
                    <FormControl fullWidth size="small" disabled={!selectedField}>
                        <InputLabel>Selecione a nova resposta</InputLabel>
                        <Select
                            value={newValue}
                            label="Selecione a nova resposta"
                            onChange={(e) => setNewValue(e.target.value)}
                        >
                            {(selectedField?.label || []).map((opt) => (
                                <MenuItem key={`${opt.code}-${opt.title}`} value={opt.code}>
                                    {opt.title}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                {/* 3. Páginas */}
                <Box>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                        Páginas
                    </Typography>
                    <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                        <InputLabel>Intervalo</InputLabel>
                        <Select
                            value={pageMode}
                            label="Intervalo"
                            onChange={(e) => setPageMode(e.target.value)}
                        >
                            <MenuItem value="all">Todas as páginas ({totalPages})</MenuItem>
                            <MenuItem value="current">Página atual ({currentPage})</MenuItem>
                            <MenuItem value="custom">Customizar</MenuItem>
                        </Select>
                    </FormControl>

                    {pageMode === "custom" && (
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Ex: 1-5, 8, 11-13"
                            value={pagesInput}
                            onChange={(e) => setPagesInput(e.target.value)}
                            error={invalids.length > 0}
                            helperText={
                                invalids.length > 0
                                    ? `Valores inválidos: ${invalids.join(", ")}`
                                    : "Informe números de página individuais ou intervalos separados por vírgula.\nExemplos válidos: 1-5, 8, 11-13, 20"
                            }
                            FormHelperTextProps={{ sx: { whiteSpace: "pre-line" } }}
                        />
                    )}

                    {pages.length > 0 && (
                        <Box sx={{ mt: 1.5, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                            {pages.slice(0, 40).map((p) => (
                                <Chip key={p} label={p} size="small" variant="outlined" />
                            ))}
                            {pages.length > 40 && (
                                <Chip label={`+${pages.length - 40}`} size="small" color="primary" />
                            )}
                        </Box>
                    )}

                    {pages.length === 0 && pageMode === "custom" && pagesInput.trim() && (
                        <Alert severity="warning" sx={{ mt: 1 }}>
                            Nenhuma página válida selecionada.
                        </Alert>
                    )}
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 2, borderTop: "1px solid var(--goia-border)" }}>
                <Button onClick={onClose} variant="outlined"  disabled={applying}>
                    Cancelar
                </Button>
                <Button
                    onClick={handleApply}
                    variant="contained"
                    disabled={!canApply || applying}
                    sx={{
                        backgroundColor: 'var(--goia-navy)',
                        '&:hover': { backgroundColor: 'var(--goia-navy-600)' },
                    }}
                >
                    {applying ? <CircularProgress size={20} color="inherit" /> : "OK"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default BatchEditModal;